import {TranslateService} from '@ngx-translate/core';
import {
  CustomerProductHistoryChangeSummary,
  CustomerProductHistoryEvent,
  CustomerProductHistoryField,
} from '../../../../../api';

const EMPTY_VALUE_KEY = 'common.empty-value';

export interface EventStyle {
  labelKey: string;
  icon: string;
  marker: string;
}

export interface HistoryChangeRow {
  label: string;
  oldValue: string;
  newValue: string;
}

// Colour alone never carries the meaning: every event also has its own icon and translated label.
export const EVENT_STYLES: Record<CustomerProductHistoryEvent, EventStyle> = {
  [CustomerProductHistoryEvent.CREATED]: {
    labelKey: 'product-customer.history.event.created',
    icon: 'pi-plus',
    marker: 'bg-green-100 text-green-700 border-green-200',
  },
  [CustomerProductHistoryEvent.UPDATED]: {
    labelKey: 'product-customer.history.event.updated',
    icon: 'pi-pencil',
    marker: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  [CustomerProductHistoryEvent.DELETED]: {
    labelKey: 'product-customer.history.event.deleted',
    icon: 'pi-trash',
    marker: 'bg-red-100 text-red-700 border-red-200',
  },
};

export function buildHistoryChangeRows(
  changes: CustomerProductHistoryChangeSummary[],
  translate: TranslateService,
): HistoryChangeRow[] {
  return changes.map(change => ({
    label: formatHistoryFieldLabel(change.field, translate),
    oldValue: formatHistoryValue(change.field, change.oldValue, translate),
    newValue: formatHistoryValue(change.field, change.newValue, translate),
  }));
}

// Field labels reuse the keys already displayed on the product form, so the timeline speaks the same language.
const FIELD_LABEL_KEYS: Record<CustomerProductHistoryField, string> = {
  [CustomerProductHistoryField.PRODUCT]: 'entity.product',
  [CustomerProductHistoryField.STARTDATE]: 'app.start-date',
  [CustomerProductHistoryField.ENDDATE]: 'app.end-date',
  [CustomerProductHistoryField.QUANTITY]: 'product-customer.quantity',
  [CustomerProductHistoryField.PRICE]: 'product-customer.unit-price',
  [CustomerProductHistoryField.MUSTINCLUDEININVOICING]: 'product-customer.include-in-invoicing',
  [CustomerProductHistoryField.DESCRIPTION]: 'product-customer.description',
  [CustomerProductHistoryField.ISACTIVE]: 'product-customer.active',
};

export function formatHistoryFieldLabel(field: CustomerProductHistoryField, translate: TranslateService): string {
  return translate.instant(FIELD_LABEL_KEYS[field]);
}

// The back stores raw invariant values (yyyy-MM-dd, dot decimals, "true"/"false"): the sentence is composed here.
export function formatHistoryValue(
  field: CustomerProductHistoryField,
  value: string | Date | null | undefined,
  translate: TranslateService,
): string {
  const raw = toRawValue(value);
  if (raw == null || raw.trim() === '') return translate.instant(EMPTY_VALUE_KEY);

  switch (field) {
    case CustomerProductHistoryField.STARTDATE:
    case CustomerProductHistoryField.ENDDATE:
      return formatIsoDate(raw);
    case CustomerProductHistoryField.PRICE:
      return formatPrice(raw);
    case CustomerProductHistoryField.MUSTINCLUDEININVOICING:
    case CustomerProductHistoryField.ISACTIVE:
      return translate.instant(raw === 'true' ? 'common.yes' : 'common.no');
    default:
      return raw;
  }
}

// The global date interceptor turns every yyyy-MM-dd string of a response into a Date, these opaque
// history values included: they are brought back to the raw form the formatting below expects. Built
// from the local getters, mirroring the local midnight the interceptor created, so the day never shifts.
function toRawValue(value: string | Date | null | undefined): string | null | undefined {
  if (!(value instanceof Date)) return value;

  const pad = (part: number) => String(part).padStart(2, '0');

  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

// Invariant dot decimal from the back ("99.9"): always shown with two decimals, like the product list.
function formatPrice(value: string): string {
  const numeric = Number(value);

  return Number.isNaN(numeric) ? `${value} €` : `${numeric.toFixed(2)} €`;
}

// Reformatted textually rather than through Date, so a timezone shift can never move the day.
function formatIsoDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
